/* Florame vs Shopify — interactive hero chrome v3.0.0.625 */
(() => {
  const page = document.querySelector('.ff-page[data-landing-version="3.0.0.625"]');
  if (!page) return;

  const clamp = (minimum, value, maximum) => Math.min(maximum, Math.max(minimum, value));

  const economicsMotionMedia = window.matchMedia('(max-width: 900px)');
  const responsiveEconomicsPlayers = Array.from(page.querySelectorAll('[data-fvs-responsive-src]'));

  const syncResponsiveEconomicsMotion = () => {
    responsiveEconomicsPlayers.forEach((player) => {
      const source = player.dataset.fvsResponsiveSrc;

      if (economicsMotionMedia.matches) {
        if (source && !player.getAttribute('src')) {
          player.setAttribute('src', source);
          player.load?.(source);
        }
        return;
      }

      player.pause?.();
      player.stop?.();
      player.removeAttribute('src');
    });
  };

  syncResponsiveEconomicsMotion();
  economicsMotionMedia.addEventListener?.('change', syncResponsiveEconomicsMotion);

  /* Expensive decorative motion only runs close to the viewport. This keeps
     the exact visuals while preventing hidden sections from consuming frames. */
  const motionRegions = Array.from(page.querySelectorAll([
    '.ff-trust-cloud',
    '.ff-value',
    '.fvs-reviews',
    '.fvs-economics',
    '.fvs-process',
    '.fvs-risk',
    '.fvs-story',
    '.fvs-final-cta',
    '.fvs-faq',
    '.fvs-footer-wrap'
  ].join(',')));
  const performanceReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!performanceReducedMotion.matches && 'IntersectionObserver' in window) {
    const motionMargin = 240;
    const syncMotionRegion = (region, isNear) => {
      region.classList.toggle('fvs-motion-offscreen', !isNear || document.hidden);
    };
    const motionRegionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => syncMotionRegion(entry.target, entry.isIntersecting));
    }, { rootMargin: `${motionMargin}px 0px`, threshold: 0 });

    motionRegions.forEach((region) => {
      const rect = region.getBoundingClientRect();
      syncMotionRegion(region, rect.bottom >= -motionMargin && rect.top <= window.innerHeight + motionMargin);
      motionRegionObserver.observe(region);
    });
    document.addEventListener('visibilitychange', () => {
      motionRegions.forEach((region) => {
        const rect = region.getBoundingClientRect();
        syncMotionRegion(region, rect.bottom >= -motionMargin && rect.top <= window.innerHeight + motionMargin);
      });
    });
  }

  const autoplayLotties = Array.from(page.querySelectorAll('dotlottie-player[autoplay]'));
  if (autoplayLotties.length && 'IntersectionObserver' in window) {
    const visibleAutoplayLotties = new Set();
    const setAutoplayLottieState = (player, shouldPlay) => {
      const applyState = () => {
        if (shouldPlay && !document.hidden && !performanceReducedMotion.matches) player.play?.();
        else player.pause?.();
      };
      if (typeof player.pause === 'function') applyState();
      else player.addEventListener('ready', applyState, { once: true });
    };
    const autoplayLottieObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visibleAutoplayLotties.add(entry.target);
        else visibleAutoplayLotties.delete(entry.target);
        setAutoplayLottieState(entry.target, entry.isIntersecting);
      });
    }, { rootMargin: '240px 0px', threshold: 0 });

    autoplayLotties.forEach((player) => {
      player.pause?.();
      player.addEventListener('ready', () => {
        setAutoplayLottieState(player, visibleAutoplayLotties.has(player));
      });
      autoplayLottieObserver.observe(player);
    });
    document.addEventListener('visibilitychange', () => {
      autoplayLotties.forEach((player) => setAutoplayLottieState(player, visibleAutoplayLotties.has(player)));
    });
  }

  const economicsMoneyHero = page.querySelector('.fvs-economics__moneyfall-hero');
  const economicsMoneyfall = page.querySelector('.fvs-economics__moneyfall');
  const economicsPanel = page.querySelector('.ff-economics__panel');
  const economicsKicker = page.querySelector('.fvs-economics-kicker');
  const economicsTitle = page.querySelector('.ff-economics__heading h2');
  const economicsLead = page.querySelector('.ff-economics__lead');
  const economicsMobileMedia = window.matchMedia('(max-width: 620px)');

  if (economicsMoneyHero && economicsMoneyfall && economicsPanel && economicsKicker && economicsTitle && economicsLead) {
    let economicsMaskFrame = 0;

    const renderEconomicsMoneyMask = () => {
      economicsMaskFrame = 0;

      if (!economicsMotionMedia.matches) {
        economicsMoneyfall.style.removeProperty('clip-path');
        economicsMoneyfall.style.removeProperty('-webkit-clip-path');
        economicsMoneyfall.style.removeProperty('--fvs-money-mask-bottom');
        economicsMoneyfall.style.removeProperty('--fvs-money-mask-gap');
        economicsPanel.style.removeProperty('--fvs-tablet-stage-shift');
        return;
      }

      const panelRect = economicsPanel.getBoundingClientRect();
      const heroRect = economicsMoneyHero.getBoundingClientRect();
      const kickerRect = economicsKicker.getBoundingClientRect();
      const titleRect = economicsTitle.getBoundingClientRect();
      const leadRect = economicsLead.getBoundingClientRect();
      if (heroRect.width <= 0 || heroRect.height <= 0) return;

      const topGap = kickerRect.top - panelRect.top;
      const curveBottom = titleRect.bottom - heroRect.top + topGap;
      const curveDepth = economicsMobileMedia.matches ? 28 : 34;
      const curveSegments = 28;
      const points = ['0px 0px', `${heroRect.width.toFixed(2)}px 0px`];

      for (let index = curveSegments; index >= 0; index -= 1) {
        const x = (heroRect.width * index) / curveSegments;
        const normalizedX = (x - (heroRect.width / 2)) / (heroRect.width / 2);
        const y = curveBottom - (curveDepth * normalizedX * normalizedX);
        points.push(`${x.toFixed(2)}px ${y.toFixed(2)}px`);
      }

      const clip = `polygon(${points.join(', ')})`;
      economicsMoneyfall.style.setProperty('clip-path', clip);
      economicsMoneyfall.style.setProperty('-webkit-clip-path', clip);
      economicsMoneyfall.style.setProperty('--fvs-money-mask-bottom', `${curveBottom.toFixed(2)}px`);
      economicsMoneyfall.style.setProperty('--fvs-money-mask-gap', `${topGap.toFixed(2)}px`);

      if (economicsMobileMedia.matches) {
        economicsPanel.style.removeProperty('--fvs-tablet-stage-shift');
      } else {
        const currentShift = Number.parseFloat(
          economicsPanel.style.getPropertyValue('--fvs-tablet-stage-shift')
        ) || 0;
        const baseLeadTop = leadRect.top - heroRect.top - currentShift;
        const subtitleShift = Math.max(0, curveBottom + topGap - baseLeadTop);
        economicsPanel.style.setProperty('--fvs-tablet-stage-shift', `${subtitleShift.toFixed(2)}px`);
      }
    };

    const scheduleEconomicsMoneyMask = () => {
      if (economicsMaskFrame) window.cancelAnimationFrame(economicsMaskFrame);
      economicsMaskFrame = window.requestAnimationFrame(renderEconomicsMoneyMask);
    };

    const economicsMaskObserver = new ResizeObserver(scheduleEconomicsMoneyMask);
    economicsMaskObserver.observe(economicsMoneyHero);
    economicsMaskObserver.observe(economicsTitle);
    economicsMotionMedia.addEventListener?.('change', scheduleEconomicsMoneyMask);
    economicsMobileMedia.addEventListener?.('change', scheduleEconomicsMoneyMask);
    window.addEventListener('resize', scheduleEconomicsMoneyMask, { passive: true });
    document.fonts?.ready?.then(scheduleEconomicsMoneyMask);
    scheduleEconomicsMoneyMask();
  }

  const payoutCoin = page.querySelector('[data-fvs-economics-coin]');
  const adviceCoins = page.querySelector('[data-fvs-economics-advice-coins]');
  if (payoutCoin && adviceCoins) {
    // The source files contain static tail frames. Mapping their real motion
    // ranges keeps both the visual finish and the following pause identical.
    const payoutActiveLastFrame = 63;
    const adviceActiveLastFrame = 49;
    const adviceDetails = adviceCoins.closest('details');
    let payoutAnimation = null;
    let adviceAnimation = null;
    let payoutCompleteSource = null;

    const isAdviceVisible = () => !adviceDetails || adviceDetails.open;

    const syncAdviceFrame = (payoutFrame, force = false) => {
      if (!force && !isAdviceVisible()) return;

      payoutAnimation = payoutCoin.getLottie?.() || payoutAnimation;
      adviceAnimation = adviceCoins.getLottie?.() || adviceAnimation;
      if (!payoutAnimation?.totalFrames || !adviceAnimation?.totalFrames) return;

      const currentPayoutFrame = Number.isFinite(payoutFrame)
        ? payoutFrame
        : payoutAnimation.currentFrame;
      const progress = clamp(0, currentPayoutFrame / payoutActiveLastFrame, 1);

      adviceCoins.seek(Math.round(progress * adviceActiveLastFrame));
    };

    const finishAdviceCycle = () => {
      if (!isAdviceVisible() || !adviceAnimation?.totalFrames) return;
      adviceCoins.seek(adviceActiveLastFrame);
    };

    const prepareAdviceSync = (force = false) => {
      const nextPayoutAnimation = payoutCoin.getLottie?.();
      const nextAdviceAnimation = adviceCoins.getLottie?.();
      if (!nextPayoutAnimation || !nextAdviceAnimation) return;

      adviceCoins.pause();
      adviceAnimation = nextAdviceAnimation;

      payoutAnimation = nextPayoutAnimation;
      if (payoutCompleteSource !== nextPayoutAnimation) {
        payoutCompleteSource?.removeEventListener('complete', finishAdviceCycle);
        payoutCompleteSource = nextPayoutAnimation;
        payoutCompleteSource.addEventListener('complete', finishAdviceCycle);
      }

      syncAdviceFrame(undefined, force);
    };

    payoutCoin.addEventListener('frame', (event) => {
      syncAdviceFrame(event.detail?.frame);
    });

    payoutCoin.addEventListener('loop', () => {
      if (isAdviceVisible()) adviceCoins.seek(0);
    });

    payoutCoin.addEventListener('ready', () => prepareAdviceSync(true));
    adviceCoins.addEventListener('ready', () => prepareAdviceSync(true));

    adviceDetails?.addEventListener('toggle', () => {
      if (!adviceDetails.open) return;
      window.requestAnimationFrame(() => prepareAdviceSync(true));
    });

    window.customElements.whenDefined('dotlottie-player').then(() => {
      window.requestAnimationFrame(() => prepareAdviceSync(true));
    });
  }

  const runAfterHeroIntro = (callback) => {
    if (!page.classList.contains('is-intro-running')) {
      window.requestAnimationFrame(callback);
      return;
    }
    page.addEventListener('fvs:hero-intro-complete', callback, { once: true });
  };

  const header = page.querySelector('[data-ff-header]');
  if (header) {
    let previousY = Math.max(0, window.scrollY);
    let direction = 0;
    let directionStartY = previousY;
    let headerFrame = 0;

    const setHeaderVisible = (visible) => {
      header.classList.toggle('is-scroll-hidden', !visible);
    };

    const syncHeaderDirection = () => {
      cancelAnimationFrame(headerFrame);
      headerFrame = requestAnimationFrame(() => {
        const currentY = Math.max(0, window.scrollY);
        const delta = currentY - previousY;
        const nextDirection = delta > 0 ? 1 : delta < 0 ? -1 : direction;

        if (nextDirection !== direction) {
          direction = nextDirection;
          directionStartY = previousY;
        }

        if (currentY <= 24) {
          setHeaderVisible(true);
        } else if (direction > 0 && currentY > 96 && currentY - directionStartY > 14) {
          setHeaderVisible(false);
        } else if (direction < 0 && directionStartY - currentY > 8) {
          setHeaderVisible(true);
        }

        previousY = currentY;
      });
    };

    header.addEventListener('focusin', () => setHeaderVisible(true));
    window.addEventListener('scroll', syncHeaderDirection, { passive: true });
    syncHeaderDirection();
  }

  const valueSection = page.querySelector('[data-ff-value-stack]');
  const valueTitle = page.querySelector('[data-fvs-value-title]');
  const valueKicker = page.querySelector('[data-fvs-value-kicker]');
  if (valueSection && valueTitle && valueKicker) {
    const valuePockets = Array.from(valueSection.querySelectorAll('[data-ff-value-pocket]'));

    /* Content-first motion: every benefit is immediately available in the DOM
       and at its final layout position. The shared class only adds a light,
       simultaneous entrance without gating fast scrolling. */
    valueKicker.classList.add('is-entered');
    valueTitle.classList.add('is-lead-entered', 'is-included-entered');
    valuePockets.forEach((pocket) => {
      pocket.classList.add(
        'is-fvs-value-pocket-released',
        'is-value-pocket-visible',
        'is-fvs-value-pocket-settled'
      );
      pocket.querySelector('[data-ff-value-card]')?.classList.add(
        'is-fvs-value-entered',
        'is-value-card-visible'
      );
    });
    window.requestAnimationFrame(() => valueSection.classList.add('is-fvs-value-entered-together'));
  }

  const useLegacyValueSequence = false;
  if (useLegacyValueSequence && valueSection && valueTitle && valueKicker) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const valuePockets = Array.from(valueSection.querySelectorAll('[data-ff-value-pocket]'));
    const valueLead = valueTitle.querySelector('.fvs-value-title__lead');

    const valueTitleBlock = valueSection.querySelector('.ff-value-title-block');
    const valueBridge = valueSection.querySelector('[data-fvs-value-bridge]');
    let valueHeaderRevealed = false;
    let valueHeaderQueued = false;
    let valueHeaderRequested = false;
    let valueBridgePending = false;
    let valueLeadRevealed = false;
    let valueFinaleRevealed = false;
    let valueCardsUnlocked = false;
    let valuePocketObserver = null;
    const queuedValueCards = new WeakSet();
    const queuedValuePockets = new Set();

    const revealValueCard = (card, onRevealed) => {
      if (!card || card.classList.contains('is-fvs-value-entered') || queuedValueCards.has(card)) return;
      queuedValueCards.add(card);

      runAfterHeroIntro(() => {
        queuedValueCards.delete(card);
        card.classList.add('is-fvs-value-entered', 'is-value-card-visible');
        onRevealed?.();
      });
    };

    const settleValuePocket = (pocket, card) => {
      if (!pocket || pocket.classList.contains('is-fvs-value-pocket-settled')) return;

      if (reducedMotion || !card) {
        pocket.classList.add('is-fvs-value-pocket-settled');
        return;
      }

      let settled = false;
      let fallbackTimer = 0;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(fallbackTimer);
        card.removeEventListener('transitionend', onTransitionEnd);
        pocket.classList.add('is-fvs-value-pocket-settled');
      };
      const onTransitionEnd = (event) => {
        if (event.target === card && event.propertyName === 'transform') finish();
      };

      card.addEventListener('transitionend', onTransitionEnd);
      fallbackTimer = window.setTimeout(finish, 1100);
    };

    const releaseValuePocket = (pocket) => {
      if (!pocket || pocket.classList.contains('is-fvs-value-pocket-released')) return;
      queuedValuePockets.delete(pocket);
      pocket.classList.add('is-fvs-value-pocket-released', 'is-value-pocket-visible');
      const card = pocket.querySelector('[data-ff-value-card]');
      revealValueCard(card, () => settleValuePocket(pocket, card));
      valuePocketObserver?.unobserve(pocket);
    };

    const releaseInitialValueCards = () => {
      valueCardsUnlocked = true;
      const releaseBoundary = window.innerHeight + Math.min(168, window.innerHeight * .18);
      const initialPockets = valuePockets.filter((pocket) => {
        const bounds = pocket.getBoundingClientRect();
        return bounds.top <= releaseBoundary && bounds.bottom >= -24;
      });

      (initialPockets.length ? initialPockets : valuePockets.slice(0, 1)).forEach(releaseValuePocket);
    };

    const startValueFinale = () => {
      if (valueFinaleRevealed) return;
      valueFinaleRevealed = true;
      valueTitle.classList.add('is-included-entered');
      releaseInitialValueCards();
    };

    const startValueLead = () => {
      if (valueLeadRevealed) return;
      valueLeadRevealed = true;

      if (reducedMotion || !valueLead) {
        valueTitle.classList.add('is-lead-entered');
        startValueFinale();
        return;
      }

      let leadFinished = false;
      const finishLead = () => {
        if (leadFinished) return;
        leadFinished = true;
        startValueFinale();
      };
      valueLead.addEventListener('animationend', (event) => {
        if (event.target === valueLead && event.animationName === 'fvsValueLeadRise') finishLead();
      }, { once: true });
      valueTitle.classList.add('is-lead-entered');
      window.setTimeout(finishLead, 920);
    };

    const revealValueHeader = () => {
      valueHeaderRequested = true;
      if (valueBridgePending) return;
      if (valueHeaderRevealed || valueHeaderQueued) return;
      valueHeaderQueued = true;

      runAfterHeroIntro(() => {
        valueHeaderQueued = false;
        if (valueHeaderRevealed) return;
        valueHeaderRevealed = true;

        if (reducedMotion) {
          valueKicker.classList.add('is-entered');
          startValueLead();
          return;
        }

        let kickerFinished = false;
        const finishKicker = () => {
          if (kickerFinished) return;
          kickerFinished = true;
          startValueLead();
        };
        valueKicker.addEventListener('animationend', (event) => {
          if (event.target === valueKicker && event.animationName === 'fvsValueKickerDrop') finishKicker();
        }, { once: true });
        valueKicker.classList.add('is-entered');
        window.setTimeout(finishKicker, 940);
      });
    };

    if (valueBridge && !reducedMotion && window.scrollY <= 4) {
      const initialValueBounds = valueSection.getBoundingClientRect();
      const valueStartsInView = initialValueBounds.top < window.innerHeight - 8 && initialValueBounds.bottom > 0;

      if (valueStartsInView) {
        valueBridgePending = true;
        valueSection.classList.add('has-fvs-value-bridge');

        const bridgeStartY = window.scrollY;
        const bridgeTravel = clamp(48, window.innerHeight * .085, 76);
        let bridgeFrame = 0;
        let bridgeDismissed = false;

        const dismissValueBridge = () => {
          if (bridgeDismissed) return;
          bridgeDismissed = true;
          valueSection.classList.add('is-fvs-value-bridge-dismissed');
          window.removeEventListener('scroll', syncValueBridge);

          let bridgeFadeFinished = false;
          const finishBridgeFade = () => {
            if (bridgeFadeFinished) return;
            bridgeFadeFinished = true;
            valueBridgePending = false;
            if (valueHeaderRequested) revealValueHeader();
          };

          valueBridge.addEventListener('transitionend', (event) => {
            if (event.target === valueBridge && event.propertyName === 'opacity') finishBridgeFade();
          }, { once: true });
          window.requestAnimationFrame(() => {
            if (parseFloat(window.getComputedStyle(valueBridge).opacity) <= .025) finishBridgeFade();
          });
          window.setTimeout(finishBridgeFade, 500);
        };

        const syncValueBridge = () => {
          cancelAnimationFrame(bridgeFrame);
          bridgeFrame = requestAnimationFrame(() => {
            const distance = Math.max(0, window.scrollY - bridgeStartY);
            const progress = clamp(0, distance / bridgeTravel, 1);
            valueSection.style.setProperty('--fvs-value-bridge-progress', progress.toFixed(3));
            valueSection.style.setProperty('--fvs-value-bridge-opacity', (1 - progress).toFixed(3));
            valueSection.style.setProperty('--fvs-value-bridge-offset', `${(-18 * progress).toFixed(2)}px`);
            valueSection.style.setProperty('--fvs-value-bridge-scale', (1 - (progress * .08)).toFixed(3));
            valueSection.style.setProperty('--fvs-value-wash-opacity', (.96 - (progress * .42)).toFixed(3));
            if (progress >= 1) dismissValueBridge();
          });
        };

        window.addEventListener('scroll', syncValueBridge, { passive: true });
        syncValueBridge();
      }
    }

    if (reducedMotion || !('IntersectionObserver' in window)) {
      revealValueHeader();
      valueCardsUnlocked = true;
      valuePockets.forEach(releaseValuePocket);
    } else {
      valueSection.classList.add('fvs-value-sequence-ready', 'fvs-value-cards-locked');

      const valueHeaderObserver = new IntersectionObserver((entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        revealValueHeader();
        window.removeEventListener('scroll', revealHeaderWhenNear);
        observer.disconnect();
      }, {
        threshold: 0.06,
        rootMargin: '0px 0px 4% 0px'
      });

      valueHeaderObserver.observe(valueTitleBlock);

      const revealHeaderWhenNear = () => {
        if (valueHeaderRevealed) return;
        const bounds = valueTitleBlock.getBoundingClientRect();
        if (bounds.top <= window.innerHeight * 1.04 && bounds.bottom >= 0) {
          revealValueHeader();
          valueHeaderObserver.disconnect();
          window.removeEventListener('scroll', revealHeaderWhenNear);
        }
      };

      window.addEventListener('scroll', revealHeaderWhenNear, { passive: true });
      window.requestAnimationFrame(revealHeaderWhenNear);

      valuePocketObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (valueCardsUnlocked) releaseValuePocket(entry.target);
          else queuedValuePockets.add(entry.target);
        });
      }, {
        threshold: 0.08,
        rootMargin: '0px 0px 8% 0px'
      });

      valuePockets.forEach((pocket) => valuePocketObserver.observe(pocket));
    }
  }

  const reviewsSection = page.querySelector('[data-fvs-reviews]');
  if (reviewsSection) {
    const reviewCards = Array.from(reviewsSection.querySelectorAll('.fvs-review-card'));
    const reviewsAvatars = reviewsSection.querySelector('[data-fvs-review-avatars]');
    const ratingStars = Array.from(reviewsSection.querySelectorAll('[data-fvs-rating-star]'));
    const ratingValue = reviewsSection.querySelector('[data-fvs-rating-value]');
    const ratingLine = reviewsSection.querySelector('[data-fvs-rating-line]');
    const reviewRatings = reviewCards.map((card) => clamp(0, Number.parseFloat(card.dataset.reviewRating) || 0, 5));
    const averageRating = reviewRatings.length
      ? reviewRatings.reduce((total, rating) => total + rating, 0) / reviewRatings.length
      : 0;
    const formattedAverageRating = averageRating.toLocaleString('it-IT', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });

    ratingStars.forEach((star, index) => {
      const starFill = clamp(0, (averageRating - index) * 100, 100);
      star.style.setProperty('--fvs-star-fill', `${starFill}%`);
    });

    if (ratingValue) ratingValue.textContent = formattedAverageRating;
    if (ratingLine) ratingLine.setAttribute('aria-label', `Valutazione media ${formattedAverageRating} su 5`);

    if (reviewsAvatars && reviewCards.length) {
      const reviewerProfiles = reviewCards.map((card) => {
        const logo = card.querySelector('.fvs-review-card__avatar img');
        const reviewer = card.querySelector('.fvs-review-card__author strong');
        const business = card.querySelector('.fvs-review-card__business');

        return {
          logo,
          name: reviewer?.textContent.trim() || 'Fiorista partner',
          business: business?.textContent.trim() || ''
        };
      }).filter((profile) => profile.logo);
      const visibleProfiles = reviewerProfiles.slice(0, 5);
      const hiddenProfiles = Math.max(0, reviewerProfiles.length - visibleProfiles.length);

      reviewsAvatars.replaceChildren();
      visibleProfiles.forEach((profile) => {
        const avatar = document.createElement('span');
        const image = profile.logo.cloneNode(true);
        const profileLabel = profile.business ? `${profile.name} — ${profile.business}` : profile.name;

        avatar.className = 'fvs-reviews__avatar';
        avatar.title = profileLabel;
        image.alt = '';
        image.width = 42;
        image.height = 42;
        avatar.append(image);
        reviewsAvatars.append(avatar);
      });

      if (hiddenProfiles) {
        const overflowAvatar = document.createElement('span');
        overflowAvatar.className = 'fvs-reviews__avatar fvs-reviews__avatar--overflow';
        overflowAvatar.textContent = `+${hiddenProfiles}`;
        overflowAvatar.setAttribute('aria-hidden', 'true');
        reviewsAvatars.append(overflowAvatar);
      }

      const reviewerNames = reviewerProfiles.map((profile) => profile.name).join(', ');
      reviewsAvatars.setAttribute('aria-label', `Recensioni di ${reviewerNames}`);
    }

    const reviewsReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const revealReviews = () => reviewsSection.classList.add('is-reviews-entered');

    if (reviewsReducedMotion || !('IntersectionObserver' in window)) {
      revealReviews();
    } else {
      reviewsSection.classList.add('fvs-reviews--motion-ready');

      const reviewsObserver = new IntersectionObserver((entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        revealReviews();
        observer.disconnect();
      }, {
        threshold: 0.22,
        rootMargin: '0px 0px -8% 0px'
      });

      reviewsObserver.observe(reviewsSection);
    }
  }

  page.querySelectorAll('[data-fvs-hero-cta]').forEach((heroCta) => {
    heroCta.addEventListener('click', (event) => {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const modifiedClick = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
      if (reducedMotion || modifiedClick || heroCta.target === '_blank') return;

      event.preventDefault();
      if (heroCta.classList.contains('is-clicked')) return;

      heroCta.classList.add('is-clicked');
      const destination = heroCta.href;
      window.setTimeout(() => window.location.assign(destination), 440);
    });
  });

  page.querySelectorAll('.fvs-faq__group').forEach((group) => {
    group.addEventListener('toggle', (event) => {
      const item = event.target;
      if (!(item instanceof HTMLDetailsElement) || !item.open) return;

      group.querySelectorAll('.fvs-faq__item[open]').forEach((otherItem) => {
        if (otherItem !== item) otherItem.removeAttribute('open');
      });
    }, true);
  });

  const trustViewport = page.querySelector('[data-ff-trust-viewport]');
  const trustTrack = trustViewport?.querySelector('[data-ff-trust-track]');
  const trustSet = trustTrack?.querySelector('[data-ff-trust-set]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const shinePhrases = [...page.querySelectorAll('.fvs-title-shine')];

  if (!reducedMotion.matches && shinePhrases.length) {
    /* Match the original CTA beam travel exactly: 62%→88% of its 4.4s cycle. */
    const shineActiveDuration = 1144;
    const shinePauseDuration = 3256;
    const shineCycleDuration = shineActiveDuration + shinePauseDuration;
    const shineStartedAt = performance.now();
    let shineMeasureFrame = 0;
    let shineFrame = 0;
    const visibleShinePhrases = new Set();

    shinePhrases.forEach((phrase) => {
      const source = phrase.textContent || '';
      const tokens = source.split(/(\s+)/).filter(Boolean);
      const fragment = document.createDocumentFragment();

      tokens.forEach((token) => {
        if (/^\s+$/.test(token)) {
          fragment.appendChild(document.createTextNode(token));
          return;
        }

        const word = document.createElement('span');
        word.className = 'fvs-title-shine__word';
        word.dataset.fvsShineWord = token;
        word.textContent = token;
        fragment.appendChild(word);
      });

      phrase.replaceChildren(fragment);
    });

    const measureShineLines = () => {
      shineMeasureFrame = 0;

      shinePhrases.forEach((phrase) => {
        const words = [...phrase.querySelectorAll('.fvs-title-shine__word')];
        const lines = [];

        words.forEach((word) => {
          const metric = {
            top: word.offsetTop,
            left: word.offsetLeft,
            width: word.offsetWidth,
          };
          let line = lines.find((candidate) => Math.abs(candidate.top - metric.top) < 2);

          if (!line) {
            line = { top: metric.top, words: [] };
            lines.push(line);
          }

          line.words.push({ word, metric });
        });

        const orderedLines = lines
          .sort((first, second) => first.top - second.top)
          .map((line) => {
            line.words.sort((first, second) => first.metric.left - second.metric.left);
            const left = Math.min(...line.words.map((item) => item.metric.left));
            const right = Math.max(...line.words.map((item) => item.metric.left + item.metric.width));

            return {
              ...line,
              left,
              width: Math.max(right - left, 1),
            };
          });
        const totalWidth = orderedLines.reduce((total, line) => total + line.width, 0) || 1;
        let completedWidth = 0;

        orderedLines.forEach((line, lineIndex) => {
          const start = completedWidth / totalWidth;
          completedWidth += line.width;
          const end = completedWidth / totalWidth;

          line.words.forEach((item) => {
            item.word.style.setProperty('--fvs-shine-line-start', start.toFixed(6));
            item.word.style.setProperty('--fvs-shine-line-end', end.toFixed(6));
            item.word.style.setProperty('--fvs-shine-line-width', `${line.width.toFixed(3)}px`);
            item.word.style.setProperty('--fvs-shine-word-offset', `${(item.metric.left - line.left).toFixed(3)}px`);
            item.word.dataset.fvsShineLine = String(lineIndex + 1);
          });
        });
      });

      page.classList.add('is-shine-ready');
    };

    const scheduleShineMeasure = () => {
      if (shineMeasureFrame) cancelAnimationFrame(shineMeasureFrame);
      shineMeasureFrame = requestAnimationFrame(measureShineLines);
    };

    const renderUnifiedShine = (now) => {
      shineFrame = 0;
      if (document.hidden || !visibleShinePhrases.size) return;
      const cycleTime = (now - shineStartedAt) % shineCycleDuration;
      const progress = Math.min(cycleTime / shineActiveDuration, 1);
      visibleShinePhrases.forEach((phrase) => {
        phrase.style.setProperty('--fvs-shine-progress', progress.toFixed(6));
      });
      shineFrame = requestAnimationFrame(renderUnifiedShine);
    };

    const startUnifiedShine = () => {
      if (!shineFrame && !document.hidden && visibleShinePhrases.size) {
        shineFrame = requestAnimationFrame(renderUnifiedShine);
      }
    };

    scheduleShineMeasure();
    window.addEventListener('resize', scheduleShineMeasure, { passive: true });
    if ('IntersectionObserver' in window) {
      const shineObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleShinePhrases.add(entry.target);
          else {
            visibleShinePhrases.delete(entry.target);
            entry.target.style.removeProperty('--fvs-shine-progress');
          }
        });
        if (!visibleShinePhrases.size && shineFrame) {
          cancelAnimationFrame(shineFrame);
          shineFrame = 0;
        }
        startUnifiedShine();
      }, { rootMargin: '180px 0px', threshold: 0 });
      shinePhrases.forEach((phrase) => shineObserver.observe(phrase));
    } else {
      shinePhrases.forEach((phrase) => visibleShinePhrases.add(phrase));
      startUnifiedShine();
    }
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && shineFrame) {
        cancelAnimationFrame(shineFrame);
        shineFrame = 0;
      } else {
        startUnifiedShine();
      }
    });
  }

  if (trustViewport && trustTrack && trustSet && !reducedMotion.matches) {
    const mobileTrust = window.matchMedia('(max-width: 620px)');
    let setWidth = 0;
    let offset = 0;
    let lastFrameTime = 0;
    let activePointer = null;
    let lastPointerX = 0;
    let pointerTravel = 0;
    let pauseUntil = 0;
    let resizeFrame = 0;
    let trustFrame = 0;
    let trustIsNear = false;

    const wrapOffset = (value) => {
      if (!setWidth) return value;
      while (value >= 0) value -= setWidth;
      while (value < -setWidth) value += setWidth;
      return value;
    };

    const renderTrustTrack = () => {
      trustTrack.style.transform = `translate3d(${offset.toFixed(3)}px, 0, 0)`;
    };

    const rebuildTrustCopies = () => {
      const previousProgress = setWidth ? offset / setWidth : -1;

      trustTrack.querySelectorAll('.ff-trust-cloud__set:not([data-ff-trust-set])').forEach((copy) => copy.remove());
      setWidth = trustSet.getBoundingClientRect().width;
      if (!setWidth) return;

      const copyCount = Math.max(2, Math.ceil(trustViewport.clientWidth / setWidth) + 2);
      for (let index = 1; index < copyCount; index += 1) {
        const copy = trustSet.cloneNode(true);
        copy.removeAttribute('data-ff-trust-set');
        copy.removeAttribute('role');
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('[role]').forEach((item) => item.removeAttribute('role'));
        trustTrack.append(copy);
      }

      offset = wrapOffset(previousProgress * setWidth);
      renderTrustTrack();
    };

    const pauseTrustAutoplay = (duration = 520) => {
      pauseUntil = performance.now() + duration;
    };

    const finishTrustDrag = (event) => {
      if (activePointer === null || (event && event.pointerId !== activePointer)) return;

      if (event && trustViewport.hasPointerCapture?.(activePointer)) {
        trustViewport.releasePointerCapture(activePointer);
      }

      activePointer = null;
      pointerTravel = 0;
      trustViewport.classList.remove('is-dragging');
      pauseTrustAutoplay();
    };

    trustTrack.classList.add('is-interactive');
    rebuildTrustCopies();

    trustViewport.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      activePointer = event.pointerId;
      lastPointerX = event.clientX;
      pointerTravel = 0;
      pauseUntil = Number.POSITIVE_INFINITY;
      trustViewport.setPointerCapture?.(activePointer);
    });

    trustViewport.addEventListener('pointermove', (event) => {
      if (event.pointerId !== activePointer) return;

      const delta = event.clientX - lastPointerX;
      lastPointerX = event.clientX;
      pointerTravel += Math.abs(delta);
      offset = wrapOffset(offset + delta);
      renderTrustTrack();

      if (pointerTravel > 3) trustViewport.classList.add('is-dragging');
      if (event.cancelable && pointerTravel > 3) event.preventDefault();
    });

    trustViewport.addEventListener('pointerup', finishTrustDrag);
    trustViewport.addEventListener('pointercancel', finishTrustDrag);
    trustViewport.addEventListener('lostpointercapture', finishTrustDrag);

    trustViewport.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      offset = wrapOffset(offset - event.deltaX);
      renderTrustTrack();
      pauseTrustAutoplay(680);
    }, { passive: false });

    trustViewport.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      offset = wrapOffset(offset + (event.key === 'ArrowRight' ? 96 : -96));
      renderTrustTrack();
      pauseTrustAutoplay(680);
    });

    const animateTrustTrack = (time) => {
      trustFrame = 0;
      if (!trustIsNear || document.hidden) return;
      if (!lastFrameTime) lastFrameTime = time;
      const elapsed = Math.min(50, time - lastFrameTime);
      lastFrameTime = time;

      if (activePointer === null && time >= pauseUntil) {
        const pixelsPerSecond = mobileTrust.matches ? 39 : 31.5;
        offset = wrapOffset(offset + ((pixelsPerSecond * elapsed) / 1000));
        renderTrustTrack();
      }

      trustFrame = requestAnimationFrame(animateTrustTrack);
    };

    const startTrustTrack = () => {
      if (!trustFrame && trustIsNear && !document.hidden) {
        lastFrameTime = performance.now();
        trustFrame = requestAnimationFrame(animateTrustTrack);
      }
    };

    const trustResizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(rebuildTrustCopies);
    });

    trustResizeObserver.observe(trustViewport);
    document.addEventListener('visibilitychange', () => {
      lastFrameTime = performance.now();
      if (document.hidden && trustFrame) {
        cancelAnimationFrame(trustFrame);
        trustFrame = 0;
      } else {
        startTrustTrack();
      }
    });
    if ('IntersectionObserver' in window) {
      const trustMotionObserver = new IntersectionObserver(([entry]) => {
        trustIsNear = entry.isIntersecting;
        if (!trustIsNear && trustFrame) {
          cancelAnimationFrame(trustFrame);
          trustFrame = 0;
        } else {
          startTrustTrack();
        }
      }, { rootMargin: '180px 0px', threshold: 0 });
      trustMotionObserver.observe(trustViewport);
    } else {
      trustIsNear = true;
      startTrustTrack();
    }
  }

  const hero = page.querySelector('.ff-hero');
  const heroCopy = page.querySelector('.ff-hero__copy');
  const heroTitleIntro = page.querySelector('.fvs-hero-title__intro');
  const heroEnvironment = page.querySelector('[data-fvs-hero-environment]');
  const photo = page.querySelector('.fvs-hero-photo');
  const headerCta = page.querySelector('.ff-header .ff-button--small');
  const trustRail = page.querySelector('.ff-trust-cloud');
  const button = page.querySelector('.ff-hero__conversion .fvs-arrow-reveal');
  const lead = page.querySelector('.ff-hero .ff-lead');
  const buttonWrap = page.querySelector('.ff-hero__conversion .has-fvs-pop-labels');
  const proofLabels = Array.from(page.querySelectorAll('.ff-hero__conversion .fvs-pop-label'));
  if (!hero || !heroCopy || !heroTitleIntro || !heroEnvironment || !photo || !headerCta || !trustRail || !button || !lead || !buttonWrap || !proofLabels.length) return;

  const mobile = window.matchMedia('(max-width: 620px)');
  const desktop = window.matchMedia('(min-width: 900px)');
  let photoFrame = 0;
  let desktopPhotoFrame = 0;
  let desktopRhythmFrame = 0;
  let environmentFrame = 0;

  const syncHeroEnvironment = () => {
    cancelAnimationFrame(environmentFrame);
    environmentFrame = requestAnimationFrame(() => {
      const pageRect = page.getBoundingClientRect();
      const trustRect = trustRail.getBoundingClientRect();
      const environmentHeight = Math.ceil(trustRect.bottom - pageRect.top);

      page.style.setProperty('--fvs-hero-environment-height', `${environmentHeight}px`);
    });
  };

  const syncDesktopHeroRhythm = () => {
    cancelAnimationFrame(desktopRhythmFrame);
    desktopRhythmFrame = requestAnimationFrame(() => {
      if (!desktop.matches) {
        hero.style.removeProperty('--fvs-desktop-hero-height');
        syncHeroEnvironment();
        return;
      }

      hero.style.removeProperty('--fvs-desktop-hero-height');

      const trustRect = trustRail.getBoundingClientRect();
      const contentGap = Math.max(36, buttonWrap.offsetTop - (lead.offsetTop + lead.offsetHeight));
      const openLabelTranslate = page.classList.contains('fvs-original-refined') ? 0 : 23;
      const openLabelBottom = Math.max(...proofLabels.map((label) => (
        label.offsetTop + label.offsetHeight + openLabelTranslate
      )));
      const labelBottomFromHero = heroCopy.offsetTop + buttonWrap.offsetTop + openLabelBottom;
      const heroHeight = Math.ceil(labelBottomFromHero + contentGap + trustRect.height);

      hero.style.setProperty('--fvs-desktop-hero-height', `${heroHeight}px`);
      syncHeroEnvironment();
    });
  };

  const syncPhoto = () => {
    cancelAnimationFrame(photoFrame);
    photoFrame = requestAnimationFrame(() => {
      if (photo.querySelector('.fvs-hero-photo__art')) {
        const heroRect = hero.getBoundingClientRect();
        const trustRect = trustRail.getBoundingClientRect();
        const photoTop = desktop.matches
          ? headerCta.getBoundingClientRect().top - heroRect.top
          : mobile.matches ? 6 : Math.max(6, heroTitleIntro.getBoundingClientRect().top - heroRect.top - 24);
        // The portrait and its fade must finish before the rail, at every breakpoint.
        hero.style.setProperty('--fvs-portrait-top', `${Math.round(photoTop)}px`);
        hero.style.setProperty('--fvs-portrait-height', `${Math.max(280, Math.round(trustRect.top - heroRect.top - photoTop - 12))}px`);
        return;
      }
      if (!mobile.matches) {
        hero.style.removeProperty('--fvs-mobile-photo-top');
        hero.style.removeProperty('--fvs-mobile-photo-height');
        hero.style.removeProperty('--fvs-mobile-photo-fade');
        return;
      }

      const heroRect = hero.getBoundingClientRect();
      const trustRect = trustRail.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const photoTop = 6;
      const photoHeight = Math.max(280, Math.round(trustRect.top - heroRect.top - photoTop));
      const fadeStart = buttonRect.top - heroRect.top - photoTop - 24;
      const fadePercent = clamp(52, Math.round((fadeStart / photoHeight) * 100), 76);

      hero.style.setProperty('--fvs-mobile-photo-top', `${photoTop}px`);
      hero.style.setProperty('--fvs-mobile-photo-height', `${photoHeight}px`);
      hero.style.setProperty('--fvs-mobile-photo-fade', `${fadePercent}%`);
    });
  };

  const syncDesktopPhotoTop = () => {
    cancelAnimationFrame(desktopPhotoFrame);
    desktopPhotoFrame = requestAnimationFrame(() => {
      if (!desktop.matches) {
        hero.style.removeProperty('--fvs-desktop-photo-top');
        hero.style.removeProperty('--fvs-desktop-photo-fade-end');
        return;
      }

      const heroRect = hero.getBoundingClientRect();
      const headerCtaRect = headerCta.getBoundingClientRect();
      const photoTop = Math.round(headerCtaRect.top - heroRect.top);

      hero.style.setProperty('--fvs-desktop-photo-top', `${photoTop}px`);

      const photoRect = photo.getBoundingClientRect();
      const titleIntroRect = heroTitleIntro.getBoundingClientRect();
      const fadeEndOffset = Math.max(0, titleIntroRect.top - photoRect.top - 6);
      const fadeEndPercent = clamp(12, (fadeEndOffset / photoRect.height) * 100, 24);

      hero.style.setProperty('--fvs-desktop-photo-fade-end', `${fadeEndPercent.toFixed(2)}%`);
    });
  };

  const photoObserver = new ResizeObserver(syncPhoto);
  photoObserver.observe(hero);
  photoObserver.observe(trustRail);

  const desktopPhotoObserver = new ResizeObserver(syncDesktopPhotoTop);
  desktopPhotoObserver.observe(hero);
  desktopPhotoObserver.observe(headerCta);

  const desktopRhythmObserver = new ResizeObserver(syncDesktopHeroRhythm);
  desktopRhythmObserver.observe(lead);
  desktopRhythmObserver.observe(buttonWrap);
  desktopRhythmObserver.observe(trustRail);

  const environmentObserver = new ResizeObserver(syncHeroEnvironment);
  environmentObserver.observe(hero);
  environmentObserver.observe(trustRail);

  mobile.addEventListener?.('change', syncPhoto);
  desktop.addEventListener?.('change', syncDesktopHeroRhythm);
  desktop.addEventListener?.('change', syncDesktopPhotoTop);
  window.addEventListener('resize', () => {
    syncPhoto();
    syncDesktopPhotoTop();
    syncDesktopHeroRhythm();
    syncHeroEnvironment();
  }, { passive: true });
  document.fonts?.ready?.then(() => {
    syncPhoto();
    syncDesktopPhotoTop();
    syncDesktopHeroRhythm();
    syncHeroEnvironment();
  });
  syncPhoto();
  syncDesktopPhotoTop();
  syncDesktopHeroRhythm();
  syncHeroEnvironment();
  runAfterHeroIntro(() => {
    syncPhoto();
    syncDesktopPhotoTop();
    syncDesktopHeroRhythm();
    syncHeroEnvironment();
  });
  [180, 600].forEach((delay) => {
    window.setTimeout(() => {
      syncPhoto();
      syncDesktopPhotoTop();
      syncDesktopHeroRhythm();
      syncHeroEnvironment();
    }, delay);
  });
})();

/* Keep each fixed-height grid bridge centred in the real whitespace between
   the visible surfaces. One ResizeObserver replaces scroll-time measurement. */
(() => {
  const page = document.querySelector('.ff-page[data-landing-version="3.0.0.625"]');
  const content = page?.querySelector('#ff-contenuto');
  const grids = Array.from(content?.querySelectorAll('[data-fvs-grid-from][data-fvs-grid-to]') || []);
  if (!page || !content || !grids.length) return;

  let gridFrame = 0;
  const anchors = new Set();

  const getRevealOffsetY = (element) => {
    const reveal = element.closest('.ff-reveal');
    const transform = reveal ? getComputedStyle(reveal).transform : 'none';
    if (!transform || transform === 'none') return 0;

    const values = transform
      .slice(transform.indexOf('(') + 1, -1)
      .split(',')
      .map(Number);
    const offset = transform.startsWith('matrix3d(') ? values[13] : values[5];
    return Number.isFinite(offset) ? offset : 0;
  };

  const syncSectionGrids = () => {
    cancelAnimationFrame(gridFrame);
    gridFrame = requestAnimationFrame(() => {
      const contentRect = content.getBoundingClientRect();

      grids.forEach((grid) => {
        const from = page.querySelector(grid.dataset.fvsGridFrom);
        const to = page.querySelector(grid.dataset.fvsGridTo);
        if (!from || !to) return;

        const fromRect = from.getBoundingClientRect();
        const toRect = to.getBoundingClientRect();
        /* Generic section reveals start 28px lower. Remove that temporary
           translation so every bridge targets its final, stable whitespace. */
        let fromBottom = fromRect.bottom - getRevealOffsetY(from);
        /* The Value cards enter with a transform. Anchor their bridge to the
           stable content edge of the cards container instead of sampling the
           animated final pocket, while preserving the exact established gap. */
        if (grid.dataset.fvsGridFromEdge === 'content-box') {
          const fromStyle = getComputedStyle(from);
          fromBottom -= (parseFloat(fromStyle.paddingBottom) || 0)
            + (parseFloat(fromStyle.borderBottomWidth) || 0);
        }
        const toTop = toRect.top - getRevealOffsetY(to);
        const center = ((fromBottom + toTop) / 2) - contentRect.top;

        grid.style.setProperty('--fvs-section-grid-y', `${center.toFixed(2)}px`);
      });
    });
  };

  const gridResizeObserver = new ResizeObserver(syncSectionGrids);
  gridResizeObserver.observe(content);
  grids.forEach((grid) => {
    const from = page.querySelector(grid.dataset.fvsGridFrom);
    const to = page.querySelector(grid.dataset.fvsGridTo);
    if (from) anchors.add(from);
    if (to) anchors.add(to);
  });
  anchors.forEach((anchor) => gridResizeObserver.observe(anchor));

  window.addEventListener('resize', syncSectionGrids, { passive: true });
  document.fonts?.ready?.then(syncSectionGrids);
  syncSectionGrids();
  [180, 600].forEach((delay) => window.setTimeout(syncSectionGrids, delay));
})();

/* Risk-removal motion mirrors the protocol stamps without coupling the two landings. */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const splitRiskText = (element) => {
    if (!element || element.dataset.fvsRiskTextSplit === 'true') return;

    const words = (element.textContent || '').trim().split(/\s+/);
    const fragment = document.createDocumentFragment();
    let characterIndex = 0;

    words.forEach((word, wordIndex) => {
      const wordElement = document.createElement('span');
      wordElement.className = 'fvs-risk__wow-word';

      Array.from(word).forEach((character) => {
        const characterElement = document.createElement('span');
        characterElement.className = 'fvs-risk__wow-char';
        characterElement.style.setProperty('--fvs-risk-char-index', characterIndex);
        characterElement.textContent = character;
        wordElement.append(characterElement);
        characterIndex += 1;
      });

      fragment.append(wordElement);
      if (wordIndex < words.length - 1) fragment.append(document.createTextNode(' '));
    });

    element.replaceChildren(fragment);
    element.dataset.fvsRiskTextSplit = 'true';
  };

  const revealRiskOnce = (target, section, visibleClass, options) => {
    if (!target) {
      section.classList.add(visibleClass);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      section.classList.add(visibleClass);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      section.classList.add(visibleClass);
      observer.disconnect();
    }, options);

    observer.observe(target);
  };

  const initialiseRiskMotion = (section) => {
    if (!section || section.dataset.fvsRiskInitialised === 'true') return;
    section.dataset.fvsRiskInitialised = 'true';

    const stampList = section.querySelector('[data-fvs-risk-stamp-list]');
    const wowText = section.querySelector('[data-fvs-risk-scroll-text]');
    const stampItems = Array.from(stampList?.querySelectorAll('li') || []);

    stampItems.forEach((item, index) => {
      item.style.setProperty('--fvs-risk-stamp-index', index);
    });
    splitRiskText(wowText);

    if (reducedMotion.matches) {
      section.classList.add('is-risk-list-visible', 'is-risk-wow-visible');
      return;
    }

    section.classList.add('fvs-risk-motion-ready');
    revealRiskOnce(stampList, section, 'is-risk-list-visible', {
      threshold: .1,
      rootMargin: '0px 0px -22% 0px'
    });
    revealRiskOnce(wowText, section, 'is-risk-wow-visible', {
      threshold: .14,
      rootMargin: '0px 0px -28% 0px'
    });
  };

  const bootRiskMotion = (root = document) => {
    root.querySelectorAll?.('[data-fvs-risk-motion]').forEach(initialiseRiskMotion);
    if (root.matches?.('[data-fvs-risk-motion]')) initialiseRiskMotion(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootRiskMotion(), { once: true });
  } else {
    bootRiskMotion();
  }

  document.addEventListener('shopify:section:load', (event) => bootRiskMotion(event.target));
})();
